import { useState } from "react";
import { format } from "date-fns";
import { Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { DatePicker } from "@/components/ui/date-picker";
import { Field, FieldLabel } from "@/components/ui/field";
import { Badge } from "@/components/ui/badge";
import { PagedCombobox } from "@/components/ui/pagedcombobox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useNotification } from "@/shared/hooks/useNotification";
import { useRecordMaterialUsage, useMaterialUsageByDate } from "../hooks/useSowingInstructions";
import { inputsApi, type Input as InventoryInput } from "@/features/inventory/services/inputsApi";
import { useActiveBatches } from "@/features/inventory/hooks/useInputBatches";
import { useDepartments } from "@/shared/hooks/useDepartments";
import { useGreenhouses } from "@/features/farms/hooks/useGreenhouses";
import { useAuthStore } from "@/features/auth/stores/authStore";
import { useApiError } from "@/shared/hooks/useApiError";
import { useFieldError, useFieldErrorActions } from "@/shared/contexts/field-error-context";
import { FormErrorSummary } from "@/shared/components/forms/FormErrorSummary";
import {
  sowingInstructionsApi,
  type MaterialUsageActivityType,
  type MaterialUsageItemRequest,
} from "../services/sowingInstructionsApi";
import type { Department } from "@/shared/services/departmentsApi";
import type { Greenhouse } from "@/features/farms/services/greenhousesApi";

interface ItemRow {
  rowId: number;
  input: InventoryInput | null;
  inputBatchId: string;
  quantityUsed: string;
  unit: string;
}

type ComboOption = { id: string; name: string; code?: string };

const UNIT_OPTIONS = [
  { value: "0", label: "Each" },
  { value: "1", label: "Kg" },
  { value: "2", label: "Gram" },
  { value: "3", label: "Litre" },
  { value: "4", label: "Millilitre" },
];

let rowCounter = 1;
const FORM_ID = "materialUsage";

const FieldMessage = ({ field }: { field: string }) => {
  const errors = useFieldError(FORM_ID, field);
  return (
    <div
      role={errors.length ? "alert" : undefined}
      className="min-h-5 pt-1 text-xs leading-snug text-red-500"
    >
      {errors.map((error) => error.userMessage).join(" ")}
    </div>
  );
};

const formatUsageDateTime = (value?: string) =>
  value ? format(new Date(value), "dd MMM yyyy HH:mm") : "—";

const formatActivity = (value?: string) =>
  value ? value.replaceAll("_", " ").toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase()) : "—";

export const MaterialUsagePage = () => {
  const notify = useNotification();
  const handleApiError = useApiError();
  const { setFieldErrors, clearForm, clearField } = useFieldErrorActions();
  const today = format(new Date(), "yyyy-MM-dd");
  const [usageDate, setUsageDate] = useState(today);
  const [notes, setNotes] = useState("");
  const [department, setDepartment] = useState<Department | null>(null);
  const [greenhouse, setGreenhouse] = useState<Greenhouse | null>(null);
  const [farmName, setFarmName] = useState("");
  const [fieldName, setFieldName] = useState("");
  const [activityType, setActivityType] = useState<MaterialUsageActivityType | null>(null);
  const [activityTypeOtherDescription, setActivityTypeOtherDescription] = useState("");
  const [purpose, setPurpose] = useState("");
  const [sourceReferenceType, setSourceReferenceType] = useState("");
  const [sourceReferenceNumber, setSourceReferenceNumber] = useState("");
  const [rows, setRows] = useState<ItemRow[]>([{ rowId: rowCounter++, input: null, inputBatchId: "", quantityUsed: "", unit: "1" }]);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [usageScope, setUsageScope] = useState<"mine" | "department" | "all">("mine");

  const departmentsQuery = useDepartments();
  const greenhousesQuery = useGreenhouses();
  const activeBatchesQuery = useActiveBatches();
  const { user, hasAnyPermission } = useAuthStore();
  const recordMutation = useRecordMaterialUsage();
  const currentUserName = user ? `${user.firstName} ${user.otherNames}`.trim() || user.username : "";
  const canUsePrivilegedViews = hasAnyPermission([
    "reports.view",
    "inventory.view",
    "sowing_instructions.approve",
    "sowing_instructions.update",
  ]);
  const selectedDepartmentId = usageScope === "department" ? (department?.id || user?.departmentId || undefined) : undefined;
  const historyQuery = useMaterialUsageByDate(usageDate, {
    mineOnly: usageScope === "mine",
    departmentId: selectedDepartmentId,
  });
  const departmentCode = department?.code?.toUpperCase();
  const isAgroforestry = departmentCode === "AGR";
  const requiresGreenhouse = departmentCode === "SOW";
  const isOtherActivity = activityType?.code?.toUpperCase() === "OTHER";

  const addRow = () => {
    setRows((r) => {
      if (r.filter((row) => !row.input && !row.quantityUsed).length >= 2) return r;
      return [...r, { rowId: rowCounter++, input: null, inputBatchId: "", quantityUsed: "", unit: "1" }];
    });
  };

  const removeRow = (rowId: number) => {
    setRows((r) => r.filter((row) => row.rowId !== rowId));
  };

  const updateRow = (rowId: number, field: keyof ItemRow, value: string | InventoryInput | null) => {
    setRows((r) => r.map((row) => row.rowId === rowId ? { ...row, [field]: value } : row));
  };

  const updateRowFields = (rowId: number, patch: Partial<ItemRow>) => {
    setRows((r) => r.map((row) => row.rowId === rowId ? { ...row, ...patch } : row));
  };

  const clearRowFieldError = (rowId: number, field: "inputId" | "quantityUsed" | "unit" | "inputBatchId") => {
    const rowIndex = rows.findIndex((row) => row.rowId === rowId);
    if (rowIndex >= 0) clearField(FORM_ID, `items[${rowIndex}].${field}`);
  };

  const handleSubmit = () => {
    clearForm(FORM_ID);
    setSubmitError(null);
    const localErrors = validateForm();
    if (localErrors.length > 0) {
      setFieldErrors(FORM_ID, localErrors);
      setSubmitError("Please correct the highlighted fields.");
      queueMicrotask(() => {
        document.querySelector<HTMLElement>("[role='alert']")?.scrollIntoView({ behavior: "smooth", block: "center" });
      });
      return;
    }

    const validRows = rows.filter((r) => r.input && r.quantityUsed);
    const items: MaterialUsageItemRequest[] = validRows.map((r) => ({
      inputId: r.input!.id,
      inputBatchId: r.inputBatchId || undefined,
      quantityUsed: parseFloat(r.quantityUsed),
      unit: parseInt(r.unit),
    }));

    recordMutation.mutate(
      {
        usageDate: new Date(`${usageDate}T00:00:00Z`).toISOString(),
        departmentId: department!.id,
        greenhouseId: requiresGreenhouse ? greenhouse?.id : undefined,
        farmName: isAgroforestry ? farmName.trim() || undefined : undefined,
        fieldName: isAgroforestry ? fieldName.trim() || undefined : undefined,
        activityTypeId: activityType!.id,
        activityType: activityType!.name,
        activityTypeOtherDescription: isOtherActivity ? activityTypeOtherDescription.trim() : undefined,
        purpose: purpose.trim(),
        sourceReferenceType: sourceReferenceType.trim() || undefined,
        sourceReferenceNumber: sourceReferenceNumber.trim() || undefined,
        notes,
        items,
      },
      {
        onSuccess: () => {
          clearForm(FORM_ID);
          notify.success("Material usage recorded");
          setRows([{ rowId: rowCounter++, input: null, inputBatchId: "", quantityUsed: "", unit: "1" }]);
          setNotes("");
          setPurpose("");
          setFarmName("");
          setFieldName("");
          setSourceReferenceNumber("");
          historyQuery.refetch();
        },
        onError: (err: unknown) => {
          const env = handleApiError(err, { formId: FORM_ID });
          setSubmitError(env.userMessage || "Please correct the highlighted fields.");
        },
      }
    );
  };

  const validateForm = () => {
    const errors: Array<{ field: string; code: string; userMessage: string }> = [];
    if (!usageDate) errors.push({ field: "usageDate", code: "REQUIRED", userMessage: "Select a usage date." });
    const selectedDate = new Date(`${usageDate}T00:00:00Z`);
    const todayUtc = new Date();
    const todayDateUtc = new Date(Date.UTC(todayUtc.getUTCFullYear(), todayUtc.getUTCMonth(), todayUtc.getUTCDate()));
    if (usageDate && selectedDate.getTime() > todayDateUtc.getTime()) {
      errors.push({ field: "usageDate", code: "INVALID_VALUE", userMessage: "Usage date cannot be in the future." });
    }
    if (!department) errors.push({ field: "departmentId", code: "REQUIRED", userMessage: "Select a department." });
    if (requiresGreenhouse && !greenhouse) errors.push({ field: "greenhouseId", code: "REQUIRED", userMessage: "Select a Greenhouse for this usage." });
    if (isAgroforestry && !farmName.trim() && !fieldName.trim()) {
      errors.push({ field: "farmName", code: "REQUIRED", userMessage: "Enter a Farm or Field/Block for Agroforestry usage." });
      errors.push({ field: "fieldName", code: "REQUIRED", userMessage: "Enter a Farm or Field/Block for Agroforestry usage." });
    }
    if (!activityType) errors.push({ field: "activityTypeId", code: "REQUIRED", userMessage: "Select an activity type." });
    if (isOtherActivity && !activityTypeOtherDescription.trim()) {
      errors.push({ field: "activityTypeOtherDescription", code: "REQUIRED", userMessage: "Describe the other activity type." });
    }
    if (!purpose.trim()) errors.push({ field: "purpose", code: "REQUIRED", userMessage: "Enter a purpose." });

    const populatedRows = rows.filter((row) => row.input || row.quantityUsed || row.inputBatchId);
    if (populatedRows.length === 0) {
      errors.push({ field: "items", code: "REQUIRED", userMessage: "Add at least one material row." });
    }

    rows.forEach((row, index) => {
      if (!row.input && !row.quantityUsed && !row.inputBatchId) return;
      if (!row.input) errors.push({ field: `items[${index}].inputId`, code: "REQUIRED", userMessage: "Select a material." });
      const quantity = Number(row.quantityUsed);
      if (!Number.isFinite(quantity) || quantity <= 0) {
        errors.push({ field: `items[${index}].quantityUsed`, code: "INVALID_VALUE", userMessage: "Quantity must be greater than zero." });
      }
      if (!row.unit) errors.push({ field: `items[${index}].unit`, code: "REQUIRED", userMessage: "Select a unit." });
    });

    return errors;
  };

  const departmentFetch = async ({ search, pageNumber, pageSize }: { search: string; pageNumber: number; pageSize: number }) => {
    const all = (departmentsQuery.data ?? []).filter((d) => d.isActive);
    const term = search.trim().toLowerCase();
    const filtered = term ? all.filter((d) => d.name.toLowerCase().includes(term) || d.code.toLowerCase().includes(term)) : all;
    return { items: filtered.slice((pageNumber - 1) * pageSize, pageNumber * pageSize) };
  };

  const greenhouseFetch = async ({ search, pageNumber, pageSize }: { search: string; pageNumber: number; pageSize: number }) => {
    const all = (greenhousesQuery.data ?? []).filter((g) => g.isActive !== false);
    const term = search.trim().toLowerCase();
    const filtered = term ? all.filter((g) =>
      (g.greenhouseName ?? "").toLowerCase().includes(term) ||
      (g.greenhouseCode ?? "").toLowerCase().includes(term) ||
      (g.location ?? "").toLowerCase().includes(term)
    ) : all;
    return { items: filtered.slice((pageNumber - 1) * pageSize, pageNumber * pageSize).map((g) => ({ ...g, name: g.greenhouseName })) };
  };

  const activityTypeFetch = async ({ search }: { search: string; pageNumber: number; pageSize: number }) => ({
    items: await sowingInstructionsApi.getMaterialUsageActivityTypes(search),
  });

  const materialFetch = async ({ search, pageNumber, pageSize }: { search: string; pageNumber: number; pageSize: number }) => {
    const page = await inputsApi.getInputsPaged(pageNumber, pageSize, search);
    return {
      items: page.items.filter((inp: InventoryInput) => {
        const typeName = (inp.inputTypeName ?? inp.inputTypeCode ?? "").toLowerCase();
        const code = (inp.inputCode ?? "").toLowerCase();
        return inp.isActive && !typeName.includes("seed") && !code.startsWith("seed");
      }).map((inp) => ({ ...inp, name: inp.inputName })),
    };
  };

  const hasValidMaterialRow = rows.some((row) => row.input && Number(row.quantityUsed) > 0 && row.unit);
  const isSubmitDisabled = recordMutation.isPending || !hasValidMaterialRow;

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Daily Material Usage</h1>
        <p className="text-muted-foreground mt-1">
          Record materials actually applied or consumed during operations. Stock warnings may appear but will not block submission.
        </p>
      </div>

      {/* Entry form */}
      <div className="rounded-lg border p-6 space-y-4">
        <FormErrorSummary formId={FORM_ID} message={submitError ?? "Please correct the highlighted fields."} />

        {currentUserName && (
          <div className="rounded-md border bg-muted/30 px-3 py-2 text-sm">
            <span className="text-muted-foreground">Recorded by: </span>
            <span className="font-medium">{currentUserName}</span>
          </div>
        )}

        <div className="grid items-start gap-4 md:grid-cols-2 lg:grid-cols-3">
          <Field className="gap-1">
            <FieldLabel>Usage Date</FieldLabel>
            <DatePicker
              value={usageDate}
              onChange={(value) => {
                setUsageDate(value);
                clearField(FORM_ID, "usageDate");
              }}
            />
            <FieldMessage field="usageDate" />
          </Field>
          <Field className="gap-1">
            <FieldLabel>Department</FieldLabel>
            <PagedCombobox<Department>
              title="department"
              value={department}
              onChange={(item) => {
                setDepartment(item);
                setGreenhouse(null);
                setFarmName("");
                setFieldName("");
                clearField(FORM_ID, "departmentId");
                clearField(FORM_ID, "greenhouseId");
                clearField(FORM_ID, "farmName");
                clearField(FORM_ID, "fieldName");
              }}
              fetchPage={departmentFetch}
              renderLabel={(item) => `${item.code} — ${item.name}`}
            />
            <FieldMessage field="departmentId" />
          </Field>
          <Field className="gap-1">
            <FieldLabel>Notes</FieldLabel>
            <Input
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Optional notes"
            />
            <div className="min-h-5" />
          </Field>
        </div>

        <div className="grid items-start gap-4 md:grid-cols-2 lg:grid-cols-3">
          <Field className="gap-1">
            <FieldLabel>Activity Type</FieldLabel>
            <PagedCombobox<MaterialUsageActivityType>
              title="activity type"
              value={activityType}
              onChange={(item) => {
                setActivityType(item);
                if (item?.code !== "OTHER") setActivityTypeOtherDescription("");
                clearField(FORM_ID, "activityTypeId");
                clearField(FORM_ID, "activityTypeOtherDescription");
              }}
              fetchPage={activityTypeFetch}
              renderLabel={(item) => `${item.code} — ${item.name}`}
            />
            <FieldMessage field="activityTypeId" />
          </Field>

          {isOtherActivity && (
            <Field className="gap-1">
              <FieldLabel>Other Activity Description</FieldLabel>
              <Input
                value={activityTypeOtherDescription}
                onChange={(e) => {
                  setActivityTypeOtherDescription(e.target.value);
                  clearField(FORM_ID, "activityTypeOtherDescription");
                }}
                placeholder="Describe activity"
              />
              <FieldMessage field="activityTypeOtherDescription" />
            </Field>
          )}

          <Field className="gap-1">
            <FieldLabel>Purpose</FieldLabel>
            <Input
              value={purpose}
              onChange={(e) => {
                setPurpose(e.target.value);
                clearField(FORM_ID, "purpose");
              }}
              placeholder="e.g. Nursery preventive spray"
            />
            <FieldMessage field="purpose" />
          </Field>

          {requiresGreenhouse && (
            <Field className="gap-1">
              <FieldLabel>Greenhouse</FieldLabel>
              <PagedCombobox<Greenhouse & ComboOption>
                title="greenhouse"
                value={greenhouse as (Greenhouse & ComboOption) | null}
                onChange={(item) => {
                  setGreenhouse(item);
                  clearField(FORM_ID, "greenhouseId");
                }}
                fetchPage={greenhouseFetch}
                renderLabel={(item) => `${item.greenhouseCode} — ${item.greenhouseName}`}
              />
              <FieldMessage field="greenhouseId" />
            </Field>
          )}

          {isAgroforestry && (
            <>
              <Field className="gap-1">
                <FieldLabel>Farm</FieldLabel>
                <Input
                  value={farmName}
                  onChange={(e) => {
                    setFarmName(e.target.value);
                    clearField(FORM_ID, "farmName");
                    clearField(FORM_ID, "fieldName");
                  }}
                  placeholder="e.g. Main farm, Demo farm"
                />
                <FieldMessage field="farmName" />
              </Field>

              <Field className="gap-1">
                <FieldLabel>Field / Agroforestry Block</FieldLabel>
                <Input
                  value={fieldName}
                  onChange={(e) => {
                    setFieldName(e.target.value);
                    clearField(FORM_ID, "fieldName");
                    clearField(FORM_ID, "farmName");
                  }}
                  placeholder="e.g. Block A, North boundary"
                />
                <FieldMessage field="fieldName" />
              </Field>
            </>
          )}

          <Field className="gap-1">
            <FieldLabel>Source Reference Type (optional)</FieldLabel>
            <Input
              value={sourceReferenceType}
              onChange={(e) => setSourceReferenceType(e.target.value)}
              placeholder="e.g. Requisition"
            />
            <div className="min-h-5" />
          </Field>

          <Field className="gap-1">
            <FieldLabel>Source Reference Number (optional)</FieldLabel>
            <Input
              value={sourceReferenceNumber}
              onChange={(e) => setSourceReferenceNumber(e.target.value)}
              placeholder="e.g. REQ-00123"
            />
            <div className="min-h-5" />
          </Field>
        </div>

        <div className="space-y-2">
          <FieldMessage field="items" />
          <div className="hidden gap-2 px-1 text-sm font-medium text-muted-foreground md:grid md:grid-cols-12">
            <span className="md:col-span-4">Input / Material</span>
            <span className="md:col-span-3">Batch (optional)</span>
            <span className="md:col-span-3">Quantity</span>
            <span className="md:col-span-1">Unit</span>
            <span className="md:col-span-1" />
          </div>

          {rows.map((row) => (
            <div key={row.rowId} className="grid items-start gap-2 rounded-md border p-3 md:grid-cols-12 md:border-0 md:p-0">
              <div className="md:col-span-4">
                <FieldLabel className="mb-1 md:hidden">Input / Material</FieldLabel>
                <PagedCombobox<InventoryInput & { name: string }>
                  title="material"
                  value={row.input ? { ...row.input, name: row.input.inputName } : null}
                  onChange={(item) => {
                    updateRowFields(row.rowId, { input: item, inputBatchId: "" });
                    clearRowFieldError(row.rowId, "inputId");
                    clearRowFieldError(row.rowId, "inputBatchId");
                  }}
                  fetchPage={materialFetch}
                  renderLabel={(item) => `${item.inputCode} — ${item.inputName} | Available: ${item.currentStock ?? 0} ${item.unitOfMeasureDisplay ?? item.unitOfMeasure}`}
                />
                <FieldMessage field={`items[${rows.findIndex((r) => r.rowId === row.rowId)}].inputId`} />
              </div>
              <div className="md:col-span-3">
                <FieldLabel className="mb-1 md:hidden">Batch (optional)</FieldLabel>
                {(() => {
                  const batches = (activeBatchesQuery.data ?? [])
                    .filter((batch) =>
                      batch.inputId === row.input?.id &&
                      batch.isActive &&
                      batch.isAvailable
                    );

                  return (
                    <Select
                      value={row.inputBatchId || "none"}
                      onValueChange={(value) => {
                        updateRow(row.rowId, "inputBatchId", value === "none" ? "" : value);
                        clearRowFieldError(row.rowId, "inputBatchId");
                      }}
                      disabled={!row.input || activeBatchesQuery.isLoading}
                    >
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder={row.input ? "No batch" : "Select material first"} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">No batch</SelectItem>
                        {batches.map((batch) => (
                          <SelectItem key={batch.id} value={batch.id}>
                            {batch.batchNumber} — {batch.currentQuantity} {batch.unitOfMeasure}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  );
                })()}
                <FieldMessage field={`items[${rows.findIndex((r) => r.rowId === row.rowId)}].inputBatchId`} />
              </div>
              <div className="md:col-span-3">
                <FieldLabel className="mb-1 md:hidden">Quantity</FieldLabel>
                <Input
                  type="number"
                  value={row.quantityUsed}
                  onChange={(e) => {
                    updateRow(row.rowId, "quantityUsed", e.target.value);
                    clearRowFieldError(row.rowId, "quantityUsed");
                  }}
                  placeholder="0"
                />
                <FieldMessage field={`items[${rows.findIndex((r) => r.rowId === row.rowId)}].quantityUsed`} />
              </div>
              <div className="md:col-span-1">
                <FieldLabel className="mb-1 md:hidden">Unit</FieldLabel>
                <Select
                  value={row.unit}
                  onValueChange={(v) => {
                    updateRow(row.rowId, "unit", v);
                    clearRowFieldError(row.rowId, "unit");
                  }}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {UNIT_OPTIONS.map((u) => (
                      <SelectItem key={u.value} value={u.value}>{u.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FieldMessage field={`items[${rows.findIndex((r) => r.rowId === row.rowId)}].unit`} />
              </div>
              <div className="flex justify-end md:col-span-1 md:pt-0">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => removeRow(row.rowId)}
                  disabled={rows.length === 1}
                >
                  <Trash2 className="h-4 w-4 text-muted-foreground" />
                </Button>
              </div>
            </div>
          ))}

          <Button variant="outline" size="sm" onClick={addRow} className="mt-1">
            <Plus className="h-4 w-4 mr-1" />
            Add Row
          </Button>
        </div>

        <div className="flex justify-end">
          <Button onClick={handleSubmit} disabled={isSubmitDisabled}>
            {recordMutation.isPending ? "Recording..." : "Record Usage"}
          </Button>
        </div>
      </div>

      {/* History for selected date */}
      <div className="space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h2 className="text-lg font-semibold">Usage on {format(new Date(usageDate + "T00:00:00"), "dd MMM yyyy")}</h2>
          {canUsePrivilegedViews && (
            <div className="flex rounded-md border p-1">
              <Button
                type="button"
                variant={usageScope === "mine" ? "default" : "ghost"}
                size="sm"
                onClick={() => setUsageScope("mine")}
              >
                My Usage
              </Button>
              <Button
                type="button"
                variant={usageScope === "department" ? "default" : "ghost"}
                size="sm"
                onClick={() => setUsageScope("department")}
              >
                Department Usage
              </Button>
              <Button
                type="button"
                variant={usageScope === "all" ? "default" : "ghost"}
                size="sm"
                onClick={() => setUsageScope("all")}
              >
                All Usage
              </Button>
            </div>
          )}
        </div>

        {historyQuery.isLoading && <p className="text-muted-foreground">Loading history...</p>}

        {!historyQuery.isLoading && (historyQuery.data ?? []).length === 0 && (
          <p className="text-muted-foreground text-sm">No usage logged for this date</p>
        )}

        {(historyQuery.data ?? []).map((log) => {
          const items = Array.isArray(log.items) ? log.items : [];
          const location = [
            log.greenhouseName ? `Greenhouse: ${log.greenhouseName}` : null,
            log.farmName ? `Farm: ${log.farmName}` : null,
            log.fieldName ? `Field/Block: ${log.fieldName}` : null,
          ].filter(Boolean).join(" • ");
          const reference = [log.sourceReferenceType, log.sourceReferenceNumber].filter(Boolean).join(" ");

          return (
            <div key={log.id} className="rounded-lg border bg-card p-5 shadow-sm">
              <div className="flex flex-wrap items-start justify-between gap-3 border-b pb-4">
                <div className="space-y-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <h3 className="text-base font-semibold">Usage on {format(new Date(log.usageDate), "dd MMM yyyy")}</h3>
                    <Badge variant="outline">{log.reconciliationStatus ?? "Unlinked"}</Badge>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    {log.departmentName || "—"} • {formatActivity(log.activityType)}
                  </p>
                </div>
                <div className="text-left text-sm md:text-right">
                  <p className="font-medium">Recorded by {log.recordedByUserName || "—"}</p>
                  <p className="text-muted-foreground">{formatUsageDateTime(log.recordedAt)}</p>
                </div>
              </div>

              <div className="grid gap-3 py-4 text-sm md:grid-cols-2 xl:grid-cols-4">
                <div>
                  <p className="text-muted-foreground">Purpose</p>
                  <p className="font-medium">{log.purpose || "—"}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Location</p>
                  <p className="font-medium">{location || "—"}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Source Reference</p>
                  <p className="font-medium">{reference || "—"}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Notes</p>
                  <p className="font-medium">{log.notes || "—"}</p>
                </div>
              </div>

              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Material Code</TableHead>
                      <TableHead>Material Name</TableHead>
                      <TableHead>Batch/Lot</TableHead>
                      <TableHead className="text-right">Quantity Used</TableHead>
                      <TableHead>Unit</TableHead>
                      <TableHead>Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {items.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={6} className="text-muted-foreground">
                          No material lines were returned for this usage record.
                        </TableCell>
                      </TableRow>
                    ) : (
                      items.map((item) => (
                        <TableRow key={item.id}>
                          <TableCell className="font-medium">{item.inputCode || "—"}</TableCell>
                          <TableCell>{item.inputName || "—"}</TableCell>
                          <TableCell className="text-muted-foreground">{item.batchNumber ?? "—"}</TableCell>
                          <TableCell className="text-right">{item.quantityUsed}</TableCell>
                          <TableCell>{item.unit}</TableCell>
                          <TableCell>
                            <Badge variant={item.warningMessage ? "destructive" : "outline"}>
                              {item.warningMessage ? "Review" : (log.reconciliationStatus ?? "Unlinked")}
                            </Badge>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
